export const WORD_SEARCH_MAX_CHARS = 255;
export const MIN_DELIMITER_FRAGMENT_CHARS = 16;
export const MIN_SINGLE_WORD_DELIMITER_CHARS = 40;
export const MISSING_CLAUSES_QUERY = "missing-clauses";

export type LocateMissReason = "not-found" | "not-unique" | "unverified";
export type WriteAction = "replace" | "insert-after";
export type LocateMode = "replace" | "write" | "find";

export type LocateHit<TRange> = { ok: true; ranges: TRange[] };
export type LocateMiss = { ok: false; reason: LocateMissReason };
export type LocateResult<TRange> = LocateHit<TRange> | LocateMiss;

export function truncateSearchText(text: string): string {
  return text.length > WORD_SEARCH_MAX_CHARS ? text.substring(0, WORD_SEARCH_MAX_CHARS) : text;
}

export function remainingSearchChunks(originalText: string): string[] {
  const truncated = truncateSearchText(originalText);
  if (truncated === originalText) return [];
  const remaining = originalText.substring(truncated.length);
  const chunks: string[] = [];
  for (let i = 0; i < remaining.length; i += WORD_SEARCH_MAX_CHARS) {
    chunks.push(remaining.substring(i, i + WORD_SEARCH_MAX_CHARS));
  }
  return chunks;
}

/** Straight and smart double quotes. Search strips these; verify must too. */
export const QUOTE_CHARS = /["“”]/g;

export function foldForVerify(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(QUOTE_CHARS, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function isFullTextVerified(rangeText: string, originalText: string): boolean {
  return foldForVerify(rangeText) === foldForVerify(originalText);
}

export function getWriteActionForQuery(query: string | undefined): WriteAction {
  return query === MISSING_CLAUSES_QUERY ? "insert-after" : "replace";
}

export function delimiterFragments(text: string): string[] {
  const fragments: string[] = [];
  for (const delimiter of [":", ";"] as const) {
    if (!text.includes(delimiter)) continue;
    const first = text.split(delimiter)[0]?.trim();
    if (first == null || first.length === 0) continue;
    if (!fragments.includes(first)) fragments.push(first);
  }
  return fragments;
}

export function shouldAcceptDelimiterMatch(firstPart: string, matchCount: number): boolean {
  if (matchCount !== 1) return false;
  const trimmed = firstPart.trim();
  if (trimmed.length < MIN_DELIMITER_FRAGMENT_CHARS) return false;
  const words = trimmed.split(/\s+/).filter((word) => word.length > 0);
  if (words.length <= 1 && trimmed.length < MIN_SINGLE_WORD_DELIMITER_CHARS) {
    return false;
  }
  return true;
}

export function locateMissMessage(
  mode: LocateMode,
  reason: LocateMissReason = "not-found"
): string {
  if (mode === "replace") return "Could not find the text to replace.";
  if (mode === "write") return "Original text not found in document";
  if (reason === "not-unique") return "Could not find a unique match in the document.";
  return "Could not find the text in the document.";
}
